import { pusherClient } from "./pusher-client";
import {
  PusherCartUpdatePayload,
  PusherPayload,
  isCompressedPayload,
} from "./types";
import { processPayload } from "./payload-decompressor";

export class PusherService {
  private static instance: PusherService;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): PusherService {
    if (!PusherService.instance) {
      PusherService.instance = new PusherService();
    }
    return PusherService.instance;
  }

  public subscribeToCartUpdates(
    cartToken: string,
    onUpdate: (payload: PusherCartUpdatePayload) => void,
  ): () => void {
    const channelName = this.getCartChannelName(cartToken);
    const channel = pusherClient.subscribe(channelName);

    if (!channel) {
      console.error(`[PusherService] Failed to subscribe to ${channelName}`);
      return () => {};
    }

    const eventHandler = (_eventName: string) => (data: PusherPayload) => {
      try {
        let processedPayload: PusherCartUpdatePayload;

        if (isCompressedPayload(data)) {
          processedPayload = processPayload<PusherCartUpdatePayload>(data);
        } else {
          processedPayload = data as PusherCartUpdatePayload;
        }

        onUpdate(processedPayload);
      } catch (error) {
        console.error(
          `[PusherService] Error processing cart update for ${cartToken}:`,
          error,
        );
        throw error;
      }
    };

    const cartUpdateHandler = eventHandler("cart_update");
    const externalDiscountHandler = eventHandler("external_discount_applied");

    channel.bind("cart_update", cartUpdateHandler);
    channel.bind("external_discount_applied", externalDiscountHandler);

    return () => {
      channel.unbind("cart_update", cartUpdateHandler);
      channel.unbind("external_discount_applied", externalDiscountHandler);
      pusherClient.unsubscribe(channelName);
    };
  }

  private getCartChannelName(cartToken: string): string {
    return `private-cart-${cartToken}`;
  }

  public isConnected(): boolean {
    return pusherClient.isConnected();
  }

  public getConnectionState(): string {
    return pusherClient.getConnectionState();
  }
}

export const pusherService = PusherService.getInstance();
